import { fixedUi } from '@/i18n/fixedUi';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import SaveSizeChartTemplateMetaModal from '@/shein-platform/modules/templates/SaveSizeChartTemplateMetaModal';
import { attributeDisplayLabel } from '@/shein-platform/utils/sheinAttributeTemplate';
import {
  SIZE_CHART_BASE_KEY,
  applySizeChartTemplatePayload,
  checkSizeMeasureValue,
  partitionSizeMeasureAttrs,
  resolveSizeChartRows,
  sanitizeSizeMeasureInput,
  sizeChartNeedsSaleAxis,
} from '@/shein-platform/utils/sheinSizeChart';
import { pickAutoRuleTemplate } from '@/shein-platform/utils/sheinTemplateAutoRule';
import { styleNoFromSupplierCode } from '@/shein-platform/utils/sheinStyleNo';
import { parseTemplateVersion } from '@/shein-platform/utils/sheinTemplateVersionGuard';
import TemplateVersionConflictMark from '@/shein-platform/modules/products/components/TemplateVersionConflictMark';
import { sheinPlatformAPI } from '@/utils/api';
import { adminAPI } from '@/utils/adminApi';
import { showToast } from '@/utils/toast';
import { chartText } from '@/i18n/chartText';
import { useTranslation } from 'react-i18next';

function fetchSizeChartTemplateList(officialMode, shopId, params) {
  if (officialMode) {
    return adminAPI.listOfficialSheinSizeChartTemplates(params);
  }
  return sheinPlatformAPI.listSizeChartTemplates(shopId, params);
}

function fetchSizeChartTemplateDetail(officialMode, shopId, id) {
  if (officialMode) {
    return adminAPI.getOfficialSheinSizeChartTemplate(id);
  }
  return sheinPlatformAPI.getSizeChartTemplate(shopId, id);
}

/**
 * 产品尺寸信息（attribute_type=2）。
 * - 有次销售属性值：横轴=尺寸属性，纵轴=次销售属性值（文档 3.6）
 * - 仅主销售：单行基础尺寸表，不关联次销售
 * - 尺码库选用 / 保存：与类目属性库一致（选中即整表替换；空选清空；保存弹窗）
 * - 格子对齐平台卖家后台：type=text、inputMode=decimal；提交 string extra_value，正数最多 2 位小数
 */
function rowHasAnyMeasure(sizeChart, rowId, measureAttrs = []) {
  const cell = sizeChart?.[rowId];
  if (!cell || typeof cell !== 'object') return false;
  return (measureAttrs || []).some((attr) => {
    const attrId = String(attr?.attributeId || '');
    return attrId && String(cell[attrId] ?? '').trim();
  });
}

export default function SizeChartPanel({
  shopId = '',
  officialMode = false,
  shopType = '',
  productTypeId = '',
  categoryId = '',
  categoryPathLabel = '',
  /** 商家货号：有值时从中提取款号，优先匹配同款号尺码库自动规则 */
  supplierCode = '',
  sizeSaleAttributeId = '',
  sizeMeasureAttrs = [],
  sizeAttr = null,
  selectedSizeIds = [],
  skcList = [],
  sizeChart = {},
  errorMessage = '',
  onChangeCell,
  onApplyChart,
  allowSaveToLibrary = true,
  /** 受控：当前选中的尺码库 id（保存商品模板后回填） */
  selectedTemplateId: controlledSelectedTemplateId,
  onSelectedTemplateIdChange,
  onSelectedTemplateVersionChange,
  versionConflict = false,
  conflictLabel = fixedUi("已更新"),
  resolveChangePreview,
  onReloadVersionConflict,
  reloadingVersion = false,
}) {
  useTranslation('workbench');
  const [templates, setTemplates] = useState([]);
  const [uncontrolledSelectedId, setUncontrolledSelectedId] = useState('');
  const selectedTemplateId = controlledSelectedTemplateId !== undefined
    ? String(controlledSelectedTemplateId || '')
    : uncontrolledSelectedId;
  const setSelectedTemplateId = (next) => {
    const resolved = typeof next === 'function' ? next(selectedTemplateId) : next;
    const id = String(resolved || '').trim();
    if (controlledSelectedTemplateId === undefined) {
      setUncontrolledSelectedId(id);
    }
    onSelectedTemplateIdChange?.(id);
  };
  const [busy, setBusy] = useState(false);
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  /** 自动规则模板详情缓存，随列表/类目/款号变化刷新 */
  const [autoRuleDetail, setAutoRuleDetail] = useState(null);
  const autoFilledRowIdsRef = useRef(new Set());
  /** 由自动规则写入的选中 id；用户手选其它模板后不再被款号匹配覆盖 */
  const autoSelectedIdRef = useRef('');
  const lastAutoStyleKeyRef = useRef(null);
  // 父组件常内联传 onApplyChart，勿放进 effect deps，否则会「填表 → 新函数 → 再填」打满更新深度
  const onApplyChartRef = useRef(onApplyChart);
  onApplyChartRef.current = onApplyChart;

  const resolvedStyleNo = useMemo(
    () => styleNoFromSupplierCode(
      supplierCode,
      templates.map((item) => item?.styleNo).filter(Boolean),
    ),
    [supplierCode, templates],
  );

  const sizeLabel = attributeDisplayLabel(sizeAttr) || chartText('publishSpec.otherSpec', '其他规格');
  const hasSecondarySaleAttr = Boolean(sizeAttr?.attributeId);
  const needsSaleAxis = sizeChartNeedsSaleAxis(sizeAttr, selectedSizeIds, skcList);
  const saleRows = useMemo(
    () => resolveSizeChartRows(selectedSizeIds, sizeAttr, skcList),
    [selectedSizeIds, sizeAttr, skcList],
  );
  const saleRowCount = hasSecondarySaleAttr && needsSaleAxis ? saleRows.length : 0;
  const { matrixAttrs, singleAttrs, unsupportedAttrs } = useMemo(
    () => partitionSizeMeasureAttrs(sizeMeasureAttrs, saleRowCount),
    [sizeMeasureAttrs, saleRowCount],
  );
  const visibleMeasureAttrs = useMemo(
    () => [...singleAttrs, ...matrixAttrs].length
      ? sizeMeasureAttrs.filter((a) => (
        singleAttrs.some((s) => String(s.attributeId) === String(a.attributeId))
        || matrixAttrs.some((m) => String(m.attributeId) === String(a.attributeId))
      ))
      : [],
    [sizeMeasureAttrs, singleAttrs, matrixAttrs],
  );
  const chartRows = hasSecondarySaleAttr
    ? (needsSaleAxis ? saleRows : [])
    : (visibleMeasureAttrs.length
      ? [{ id: SIZE_CHART_BASE_KEY, name: '尺寸', custom: false }]
      : []);
  const chartRowKey = chartRows.map((row) => String(row.id)).join('|');

  const canEdit = visibleMeasureAttrs.length > 0 && chartRows.length > 0;

  useEffect(() => {
    autoFilledRowIdsRef.current = new Set();
    setAutoRuleDetail(null);
    autoSelectedIdRef.current = '';
    lastAutoStyleKeyRef.current = null;
  }, [shopId, officialMode, productTypeId]);

  const refreshTemplates = async () => {
    if ((!officialMode && !shopId) || !productTypeId) return;
    try {
      const res = await fetchSizeChartTemplateList(officialMode, shopId, {
        productTypeId: String(productTypeId),
        page: 1,
        pageSize: 100,
        ...(officialMode ? { shopType } : {}),
      });
      setTemplates(Array.isArray(res?.items) ? res.items : []);
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    if ((!officialMode && !shopId) || !productTypeId) {
      setTemplates([]);
      // 受控模式下由父组件在切类目时清空，避免编辑回填被此处冲掉
      if (controlledSelectedTemplateId === undefined) {
        setUncontrolledSelectedId('');
      }
      setAutoRuleDetail(null);
      return undefined;
    }
    let cancelled = false;
    fetchSizeChartTemplateList(officialMode, shopId, {
      productTypeId: String(productTypeId),
      page: 1,
      pageSize: 100,
      ...(officialMode ? { shopType } : {}),
    }).then((res) => {
      if (cancelled) return;
      setTemplates(Array.isArray(res?.items) ? res.items : []);
    }).catch(() => {
      if (!cancelled) setTemplates([]);
    });
    return () => { cancelled = true; };
  }, [shopId, officialMode, shopType, productTypeId, controlledSelectedTemplateId]);

  // 缓存当前类目下自动规则模板：有货号则按款号优先，否则按类目优先级
  useEffect(() => {
    if ((!officialMode && !shopId) || !productTypeId) {
      setAutoRuleDetail(null);
      return undefined;
    }
    const styleKey = String(resolvedStyleNo || '').trim().toLowerCase();
    const picked = pickAutoRuleTemplate(templates, { styleNo: resolvedStyleNo });
    if (!picked?.id) {
      setAutoRuleDetail(null);
      return undefined;
    }
    let cancelled = false;
    fetchSizeChartTemplateDetail(officialMode, shopId, picked.id)
      .then((res) => {
        if (cancelled) return;
        const item = res?.item || {};
        setAutoRuleDetail({
          id: String(picked.id),
          name: item.name || picked.name || '',
          payload: item.payload && typeof item.payload === 'object' ? item.payload : {},
        });
        const currentId = String(selectedTemplateId || '').trim();
        // 首次回填锁定款号，避免编辑态立刻覆盖已选模板；款号变化后再切换
        if (lastAutoStyleKeyRef.current === null) {
          lastAutoStyleKeyRef.current = styleKey;
          if (!currentId) {
            setSelectedTemplateId(String(picked.id));
            autoSelectedIdRef.current = String(picked.id);
            onSelectedTemplateVersionChange?.(parseTemplateVersion(item.version));
          } else {
            autoSelectedIdRef.current = currentId;
          }
          return;
        }
        const styleChanged = styleKey !== lastAutoStyleKeyRef.current;
        const canReplaceSelection = styleChanged
          || !currentId
          || currentId === autoSelectedIdRef.current;
        if (canReplaceSelection) {
          lastAutoStyleKeyRef.current = styleKey;
          if (currentId !== String(picked.id)) {
            autoFilledRowIdsRef.current = new Set();
          }
          setSelectedTemplateId(String(picked.id));
          autoSelectedIdRef.current = String(picked.id);
          onSelectedTemplateVersionChange?.(parseTemplateVersion(item.version));
        } else {
          lastAutoStyleKeyRef.current = styleKey;
        }
      })
      .catch(() => {
        if (!cancelled) setAutoRuleDetail(null);
      });
    return () => { cancelled = true; };
    // selectedTemplateId 仅用于判断是否已有回填，不纳入依赖以免循环
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shopId, officialMode, productTypeId, templates, resolvedStyleNo]);

  // 用户每选中尺码（出现新行）时：有自动规则且模板有对应数据则自动填充（不覆盖已填）
  useEffect(() => {
    if (!canEdit || !autoRuleDetail?.payload) return;

    const liveIds = new Set(chartRows.map((row) => String(row.id)));
    autoFilledRowIdsRef.current = new Set(
      [...autoFilledRowIdsRef.current].filter((id) => liveIds.has(id)),
    );

    const pendingRows = chartRows.filter((row) => {
      const id = String(row.id);
      if (autoFilledRowIdsRef.current.has(id)) return false;
      if (rowHasAnyMeasure(sizeChart, id, sizeMeasureAttrs)) {
        autoFilledRowIdsRef.current.add(id);
        return false;
      }
      return true;
    });
    if (!pendingRows.length) return;

    const { sizeChart: patch, filled } = applySizeChartTemplatePayload(
      autoRuleDetail.payload,
      pendingRows,
      sizeMeasureAttrs,
    );
    pendingRows.forEach((row) => {
      autoFilledRowIdsRef.current.add(String(row.id));
    });
    if (!filled || !patch || !Object.keys(patch).length) return;

    onApplyChartRef.current?.(patch, { replace: false });
    showToast(chartText('sizeChartPanel.autoFilled', `已按尺码库「${autoRuleDetail.name || autoRuleDetail.id}」自动填充尺寸`, { name: autoRuleDetail.name || autoRuleDetail.id }), 'success');
    // sizeChart / chartRows 仅通过 key 与内容读取；onApplyChart 用 ref，避免引用变化重复触发
    // eslint-disable-next-line react-hooks/exhaustive-deps -- chartRowKey 代表行集合
  }, [canEdit, autoRuleDetail, chartRowKey, sizeMeasureAttrs]);

  const applyTemplate = async (templateId) => {
    const id = String(templateId || '').trim();
    if ((!officialMode && !shopId) || !id) return;
    setBusy(true);
    try {
      const res = await fetchSizeChartTemplateDetail(officialMode, shopId, id);
      const item = res?.item || {};
      const payload = item.payload || {};
      const { sizeChart: nextChart, filled } = applySizeChartTemplatePayload(
        payload,
        chartRows,
        sizeMeasureAttrs,
      );
      // 整表替换：空模板则清空产品尺寸
      onApplyChart?.(nextChart || {}, { replace: true });
      onSelectedTemplateVersionChange?.(parseTemplateVersion(item.version));
      showToast(filled ? chartText('sizeChartPanel.appliedCount', `已套用尺码库，填入 ${filled} 项`, { count: filled }) : chartText('sizeChartPanel.emptyCleared', '尺码库为空，已清空产品尺寸'), 'success');
    } catch (e) {
      showToast(e?.message || chartText('sizeChartPanel.applyFail', '套用尺码库失败'), 'error');
    } finally {
      setBusy(false);
    }
  };

  const onTemplateSelect = (templateId) => {
    const id = String(templateId || '').trim();
    setSelectedTemplateId(id);
    if (id) {
      applyTemplate(id);
      return;
    }
    onSelectedTemplateVersionChange?.(null);
    // 下拉选空：直接清除产品尺寸
    onApplyChart?.({}, { replace: true });
  };

  const openSaveModal = () => {
    if (!allowSaveToLibrary) return;
    if (!officialMode && !shopId) {
      showToast(chartText('returnList.pickShop', fixedUi("请先选择店铺")), 'error');
      return;
    }
    if (!categoryId || !productTypeId) {
      showToast(chartText('publishAttr.pickCategory', '请先选择商品类目'), 'error');
      return;
    }
    if (!sizeChart || !Object.keys(sizeChart).length) {
      showToast(chartText('sizeChartPanel.fillBeforeSave', '请先填写产品尺寸再保存到尺码库'), 'error');
      return;
    }
    setSaveModalOpen(true);
  };

  let emptyHint = '';
  if (!categoryId) {
    emptyHint = chartText('sizeChartPanel.pickCategory', '请先选择类目');
  } else if (sizeMeasureAttrs.length === 0) {
    emptyHint = chartText('sizeChartPanel.noMeasure', '当前类目暂无尺寸属性（attribute_type=2），无需填写尺码表');
  } else if (hasSecondarySaleAttr && !needsSaleAxis) {
    emptyHint = chartText('sizeChartPanel.pickSpecFirst', `请先在规格中选择${sizeLabel}，尺码表将按所选属性值展开`, { label: sizeLabel });
  } else if (unsupportedAttrs.length && !visibleMeasureAttrs.length) {
    emptyHint = chartText('sizeChartPanel.singleApiBlock', `平台单选尺寸属性「${unsupportedAttrs.map((a) => attributeDisplayLabel(a) || a.attributeId).join('、')}」在多尺码下 API 无法提交；请只保留一个尺码，或仅使用其它尺寸列`, { names: unsupportedAttrs.map((a) => attributeDisplayLabel(a) || a.attributeId).join('、') });
  }

  const unsupportedHint = unsupportedAttrs.length > 0 && visibleMeasureAttrs.length > 0
    ? (
      <p className="spo-wizard__inline-hint">
        {chartText('sizeChartPanel.singleApiSkip', `平台单选尺寸属性「${unsupportedAttrs.map((a) => attributeDisplayLabel(a) || a.attributeId).join('、')}」在多尺码下 API 无法提交（已自动跳过，不影响其它尺寸列）`, { names: unsupportedAttrs.map((a) => attributeDisplayLabel(a) || a.attributeId).join('、') })}
      </p>
    )
    : null;

  return (
    <div className="spo-size-chart">
      <div className="spo-size-chart__head">
        <div className="spo-size-chart__title-row">
          <h3>{chartText('sizeChartPanel.title', fixedUi("产品尺寸信息"))}</h3>
        </div>
        {errorMessage ? <div className="spo-field__error" role="alert">{errorMessage}</div> : null}

      </div>

      {!canEdit ? (
        <p className="spo-wizard__inline-hint">{emptyHint}</p>
      ) : (
        <>
          <div className={`spo-size-chart-template-bar spo-attr-template-bar${versionConflict ? ' spo-attr-template-bar--version-conflict' : ''}`}>
            <TemplateVersionConflictMark
              active={versionConflict}
              label={conflictLabel === '已更新' ? chartText('photoLib.updatedMark', '已更新') : conflictLabel}
              reloading={reloadingVersion}
              resolveChangePreview={resolveChangePreview}
              onReload={onReloadVersionConflict}
            />
            <select
              className="spo-select"
              value={selectedTemplateId}
              disabled={busy || (!officialMode && !shopId)}
              onChange={(e) => onTemplateSelect(e.target.value)}
            >
              <option value="">{chartText('sizeChartPanel.pickLib', fixedUi("选择尺码库"))}</option>
              {templates.map((item) => (
                <option key={item.id} value={item.id}>{item.name}</option>
              ))}
            </select>
            {allowSaveToLibrary ? (
              <button
                type="button"
                className="spo-btn spo-btn--primary spo-btn--sm"
                disabled={busy || (!officialMode && !shopId) || !categoryId}
                onClick={openSaveModal}
              >
                {chartText('shop.save', '保存')}
              </button>
            ) : null}
          </div>
          {unsupportedHint}
          {chartRows.length > 0 && visibleMeasureAttrs.length > 0 ? (
            <div className="spo-publish__table-wrap spo-publish__table-wrap--scroll">
              {singleAttrs.length > 0 ? (
                <p className="spo-wizard__inline-hint">
                  {chartText('sizeChartPanel.sharedCols', '标「共用」的列为平台单选：合并填写一次')}
                </p>
              ) : null}
              <SizeChartTable
                rows={chartRows}
                measureAttrs={visibleMeasureAttrs}
                singleAttrIds={new Set(singleAttrs.map((a) => String(a.attributeId)))}
                sizeChart={sizeChart}
                sizeLabel={needsSaleAxis ? sizeLabel : chartText('sizeChartPanel.sizeWord', fixedUi("尺寸"))}
                onChangeCell={onChangeCell}
              />
            </div>
          ) : null}
        </>
      )}

      {allowSaveToLibrary ? (
        <SaveSizeChartTemplateMetaModal
          open={saveModalOpen}
          shopId={shopId}
          officialMode={officialMode}
          shopType={shopType}
          categoryId={categoryId}
          productTypeId={productTypeId}
          categoryPathLabel={categoryPathLabel}
          sizeSaleAttributeId={sizeSaleAttributeId || sizeAttr?.attributeId || ''}
          sizeChart={sizeChart}
          chartRows={chartRows}
          measureAttrs={sizeMeasureAttrs}
          sizeAttr={sizeAttr}
          selectedSizeIds={selectedSizeIds}
          skcList={skcList}
          onClose={() => setSaveModalOpen(false)}
          onSaved={refreshTemplates}
        />
      ) : null}
    </div>
  );
}

function SizeChartTable({
  rows,
  measureAttrs,
  singleAttrIds = new Set(),
  sizeChart,
  sizeLabel,
  onChangeCell,
}) {
  const readSharedCell = (attrId) => {
    const fromBase = String(sizeChart?.[SIZE_CHART_BASE_KEY]?.[attrId] || '').trim();
    if (fromBase) return fromBase;
    return rows
      .map((row) => String(sizeChart?.[row.id]?.[attrId] || '').trim())
      .find(Boolean) || '';
  };

  return (
    <table className="spo-publish__table spo-size-chart-table">
      <thead>
        <tr>
          <th className="spo-size-chart-table__size">{sizeLabel}</th>
          {measureAttrs.map((attr) => {
            const shared = singleAttrIds.has(String(attr.attributeId));
            return (
              <th key={attr.attributeId}>
                {attributeDisplayLabel(attr) || attr.attributeId}
                {attr.required ? <span className="spo-size-chart-table__req"> *</span> : null}
                {shared ? <span className="spo-size-chart-table__shared"> {chartText('sizeChartPanel.shared', fixedUi("共用"))}</span> : null}
                <span className="spo-size-chart-table__unit"> (cm)</span>
              </th>
            );
          })}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, rowIndex) => (
          <tr key={row.id}>
            <td className="spo-size-chart-table__size">{row.id === SIZE_CHART_BASE_KEY ? chartText('sizeChartPanel.sizeWord', fixedUi("尺寸")) : row.name}</td>
            {measureAttrs.map((attr) => {
              const attrId = String(attr.attributeId);
              const shared = singleAttrIds.has(attrId);
              if (shared) {
                if (rowIndex !== 0) return null;
                const cell = readSharedCell(attr.attributeId);
                const invalid = Boolean(cell) && !checkSizeMeasureValue(cell, attr).ok;
                return (
                  <td
                    key={attr.attributeId}
                    rowSpan={rows.length}
                    className="spo-size-chart-table__merged"
                  >
                    <input
                      className="spo-input"
                      type="text"
                      inputMode="decimal"
                      value={cell}
                      onChange={(e) => {
                        onChangeCell?.(
                          SIZE_CHART_BASE_KEY,
                          attr.attributeId,
                          sanitizeSizeMeasureInput(e.target.value, attr),
                        );
                      }}
                      placeholder="—"
                      aria-invalid={invalid ? 'true' : undefined}
                      title={chartText('sizeChartPanel.sharedTitle', '所有尺码共用此值')}
                    />
                  </td>
                );
              }
              const cell = sizeChart?.[row.id]?.[attr.attributeId] || '';
              const invalid = Boolean(cell) && !checkSizeMeasureValue(cell, attr).ok;
              return (
                <td key={attr.attributeId}>
                  <input
                    className="spo-input"
                    type="text"
                    inputMode="decimal"
                    value={cell}
                    onChange={(e) => {
                      onChangeCell?.(
                        row.id,
                        attr.attributeId,
                        sanitizeSizeMeasureInput(e.target.value, attr),
                      );
                    }}
                    placeholder="—"
                    aria-invalid={invalid ? 'true' : undefined}
                  />
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
